import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { useMemo } from 'react';
import type { TransactionType } from '@/types/legacy';
import { useCursorPagination } from '@/hooks/useCursorPagination';
import type { MarketTransactionsViewerScope } from '../config/queryKey';
import type { MarketTransactionType } from '../transactions.contract';
import { LEGACY_TO_MARKET_TRANSACTION_TYPE } from '../config';
import { fetchMarketTransactionsPage, marketTransactionsTableQueryKey } from '../config/queryKey';
interface UseTableTransactionDataParams {
  chainId: ChainId;
  marketId: string;
  first: number;
  filteredTransactionTypes: TransactionType[];
  /**
   * Viewer filter:
   * - `undefined` → no viewer filter (global market activity feed)
   * - `string`    → viewer-scoped to this address ("Your transactions")
   * - `null`      → viewer-scoped, but wallet disconnected → query disabled
   */
  userAddress?: Address | null;
  timestampGte?: number;
  timestampLte?: number;
}

/**
 * Cursor-paginated data hook for the market activity table, over
 * `market.transactions`.
 *
 * The legacy `TransactionType.Market*` filter values are translated to the
 * procedure's `type` values. Each row carries the vault, with its name and
 * asset, whenever the user address is a vault.
 * @internal
 */
export const useTableTransactionData = t0 => {
  const $ = _c(22);
  const {
    chainId,
    marketId,
    first,
    filteredTransactionTypes,
    userAddress,
    timestampGte,
    timestampLte
  } = t0;
  let t1;
  bb0: {
    if (filteredTransactionTypes.length === 0) {
      t1 = undefined;
      break bb0;
    }
    let t2;
    if ($[0] !== filteredTransactionTypes) {
      t2 = filteredTransactionTypes.map(_temp).filter(isDefined);
      $[0] = filteredTransactionTypes;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const mapped = t2;
    t1 = mapped.length > 0 ? mapped : undefined;
  }
  const types = t1;
  const apiUserAddress = userAddress ?? undefined;
  let t2;
  if ($[2] !== apiUserAddress || $[3] !== chainId || $[4] !== first || $[5] !== marketId || $[6] !== timestampGte || $[7] !== timestampLte || $[8] !== types) {
    t2 = {
      chainId,
      marketId,
      userAddress: apiUserAddress,
      types,
      timestampGte,
      timestampLte,
      limit: first
    };
    $[2] = apiUserAddress;
    $[3] = chainId;
    $[4] = first;
    $[5] = marketId;
    $[6] = timestampGte;
    $[7] = timestampLte;
    $[8] = types;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const queryFilters = t2;
  const viewerScope = userAddress === undefined ? "all" : "viewer";
  let t3;
  if ($[10] !== queryFilters || $[11] !== viewerScope) {
    t3 = marketTransactionsTableQueryKey(queryFilters, viewerScope);
    $[10] = queryFilters;
    $[11] = viewerScope;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== queryFilters) {
    t4 = cursor => fetchMarketTransactionsPage(queryFilters, cursor);
    $[13] = queryFilters;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = userAddress !== null;
  let t6;
  if ($[15] !== types) {
    t6 = types?.join(",") ?? "-";
    $[15] = types;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = `${chainId}:${marketId}:${apiUserAddress ?? "-"}:${t6}:${timestampGte ?? "-"}:${timestampLte ?? "-"}:${first}:${viewerScope}`;
  let t8;
  if ($[17] !== t3 || $[18] !== t4 || $[19] !== t5 || $[20] !== t7) {
    t8 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      resetKey: t7
    };
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return useCursorPagination(t8);
};
export default useTableTransactionData;
function _temp(t) {
  return LEGACY_TO_MARKET_TRANSACTION_TYPE[t];
}